import { axiosInstance as api } from '../../api/Middleware'
import { NatureAbsence, DocumentData, DocumentInfo, DureeCalculee } from './NouvelleDemande.types'

import { AbsenceApi }  from '../../api/Absence/AbsenceApi'

import { PlagehoraireApi } from '../../api/Plagehoraire'

import { NatureabsenceApi } from '../../api/Natureabsence'
import { NatureAbsenceType } from '../../api/Natureabsence/types'

import { DemandeAbsenceApi } from '../../api/DemandeAbsence'

import { PlageHoraireType } from '../../api/Plagehoraire/types'

import { NotifyType } from '../../components/Notification/Notification.types';
import { downloadFile } from '../../lib/generique'
import { DotationEsag } from '../../api/Dotation/types'
import { ResponseGetAllType } from '../../api/response'
import { DemandeAbsenceType } from '../../api/DemandeAbsence/types'

export const nouvelleDemandeModeActions = {
  // Calculer la durée d'une absence
  async calculerDuree(
    identifiantAgent: number,
    dateDebut: string,
    dateFin: string,
    heureDebut: number,
    heureFin: number,
    identifiantNatureAbsence: string | number,
    notify?: NotifyType
  ): Promise<DureeCalculee> {
    try {
      const resp = await AbsenceApi.getDuree(`identifiantAgent=${identifiantAgent}&dateRefDebut=${dateDebut}&dateRefFin=${dateFin}&heureRefDebut=${heureDebut}&heureRefFin=${heureFin}&identifiantNatureAbsence=${identifiantNatureAbsence}`)
      return resp
    } catch (error) {
      notify?.error('Erreur lors du calcul de la durée : ' + error, 2000)
      throw error
    }
  },

  // Récupérer les plages horaires pour un agent
  async getPlagesHoraires(
    identifiantAgent: number,
    dateReference: string,
    mode?: string,
    debut?: boolean,
    notify?: NotifyType
  ): Promise<PlageHoraireType[]> {
    try {
      let complement = ''
      if (mode) {
        complement = `&mode=${mode}`
      } else if (debut) {
        complement = '&debut=1'
      }
      const resp: PlageHoraireType[] = await PlagehoraireApi.loadHeures(`identifiantAgent=${identifiantAgent}&dateReference=${dateReference}${complement}`)

      return resp || []
    } catch (error) {
      notify?.error('Erreur lors de la récupération des plages horaires:' + error, 2000)
      throw error
    }
  },

  // Récupérer les natures d'absence
  async getNaturesAbsences(identifiantEntite: number, notify?: NotifyType): Promise<NatureAbsence[]> {
    try {
      const resp: ResponseGetAllType<NatureAbsenceType> = await NatureabsenceApi.loadNatureabsence(`decentralisableSeulement=1&actif=1&identifiantEntite=${identifiantEntite}&perPage=999`)

      if (resp.data) {
        const naturesAbsences: NatureAbsence[] = []
        resp.data.forEach((item: NatureAbsenceType) => {
          naturesAbsences.push({
            value: item.identifiant,
            label: item.libelle,
            code: item.code,
            ordre: item.ordre || 0,
          })
        })
        naturesAbsences.sort((a, b) => a.ordre - b.ordre)
        return naturesAbsences
      }
      else {
        notify?.error('Aucune nature d\'absence trouvée.', 2000)
      }
      return []
    } catch (error) {
      notify?.error('Erreur lors du chargement des types d\'absence:' + error, 2000)
      throw error
    }
  },

  // Récupérer la dotation pour un agent
  async getDotation(
    identifiantAgent: number,
    identifiantEntite: number,
    code: string,
    annee: number,
    notify?: NotifyType
  ): Promise<DotationEsag | null> {
    try {
      const isCP = code === 'CP'
      const typeAPI = isCP ? 'sconges' : 'squotas'
      const complement = isCP ? '' : `&withNatureAbsence=1&natureAbsenceCode=${code}`

      const resp = await api.get(
        `/personnel/personnel/${typeAPI}/dotationEsag?identifiantAgent=${identifiantAgent}&identifiantEntite=${identifiantEntite}&dateAcquisition=${annee}0101${complement}`
      )

      if (resp && resp.data) {
        if (resp.data.length === 0) {
          notify?.error('Vous ne possédez pas de droit pour cette nature d\'absence.', 2000)
        }
        return resp.data
      }
      return null
    } catch (error) {
      notify?.error('Erreur lors du chargement des droits:' + error, 2000)
      throw error
    }
  },

  // Créer une nouvelle demande d'absence
  async creerDemande(data: DemandeAbsenceType, identifiantEntite: number, documentData?: DocumentData, notify?: NotifyType) {
    try {
      console.log('Creating demande with data:', data)
      let response;

      if (documentData && documentData.file) {
        // Créer avec document
        response = await DemandeAbsenceApi.createDemandeAbsence(data, documentData, identifiantEntite, notify);
      } else {
        // Créer sans document
        response = await DemandeAbsenceApi.createDemandeAbsence(data);
      }

      notify?.success('Demande d\'absence créée avec succès')
      return response
    } catch (error) {
      notify?.error('Erreur lors de la création de la demande:' + error, 2000)
      console.error(error)
      throw error
    }
  },

  // Modifier une demande d'absence existante
  async modifierDemande(identifiant: number, data: DemandeAbsenceType, identifiantEntite: number, documentData?: DocumentData, deleteDocument?: boolean, notify?: NotifyType) {
    try {
      let response;

      if (documentData || deleteDocument) {
        // Modifier avec gestion de document
        response = await DemandeAbsenceApi.updateDemandeAbsence(identifiant, data, identifiantEntite, documentData, deleteDocument, notify);
      } else {
        // Modifier sans gestion de document
        response = await DemandeAbsenceApi.updateDemandeAbsence(identifiant, data);
      }

      notify?.success('Demande d\'absence modifiée avec succès')
      return response
    } catch (error) {
      notify?.error('Erreur lors de la modification de la demande:' + error, 2000)
      throw error
    }
  },

  // Supprimer une demande d'absence
  async supprimerDemande(identifiant: number, notify?: NotifyType) {
    try {
      const response: any = await DemandeAbsenceApi.deleteDemandeAbsence(identifiant);
      notify?.success('Demande d\'absence supprimée avec succès')
      return response.statut
    } catch (error) {
      notify?.error('Erreur lors de la suppression de la demande:' + error, 2000)
      throw error
    }
  },

  // Télécharger le document associé à une demande
  async telechargerDocument(identifiant: number, docInfo: DocumentInfo, notify?: NotifyType) {
    try {
      const blob = await DemandeAbsenceApi.downloadDocument(identifiant, notify);
      const savedPath = await downloadFile(blob, docInfo.filename || `document_demande${docInfo.extension}`, `/personnel/personnel/demandesabsences/${identifiant}/document`);

      notify?.success('Document téléchargé avec succès')
      return savedPath;
    } catch (error) {
      notify?.error('Erreur lors du téléchargement du document:' + error, 2000)
      throw error
    }
  },

  // Récupérer les informations d'un document
  async getDocumentInfo(identifiant: number, notify?: NotifyType): Promise<DocumentInfo | null> {
    try {
      const documentInfo = await DemandeAbsenceApi.getDocumentInfo(identifiant, notify);
      return documentInfo;
    } catch (error: any) {
      // Gérer spécifiquement les erreurs 404 (pas de document)
      if (error?.response?.status === 404 || error?.message?.includes('404')) {
        console.log('Aucun document trouvé pour cette demande (404)');
        return null;
      }

      // Pour les autres erreurs, les logger mais ne pas notifier l'utilisateur
      console.log('Erreur lors de la récupération des infos du document:', error);
      return null;
    }
  }
}
